import { TChartDataItemList, TMonthlyChart } from '../valueTypes';
import React, { useMemo } from 'react';
import { AgCharts } from 'ag-charts-react';
import { AgChartOptions } from 'ag-charts-community';
import { Card, CardBody } from '@heroui/react';
import ExpandableContainer from '../../../shared/components/expandable-container/ExpandableContainer';
import { ChartContainer } from '../../../shared/components/charts/ChartContainer';

export interface IMonthlyChartProps {
	monthlyChart: TMonthlyChart;
	panelOffset?: { top: number; left: number };
}

const MonthlyChart: React.FC<IMonthlyChartProps> = ({ monthlyChart, panelOffset = { top: 0, left: 0 } }) => {
	const data = Array.isArray(monthlyChart.chartDataItemList) ? monthlyChart.chartDataItemList : ([] as TChartDataItemList[]);

	// Filter out null values for chart data and exclude realisedSales when 0
	const sanitizedData = useMemo(() => {
		return data.map((item) => ({
			...item,
			fairMarketValue: item.fairMarketValue ?? 0,
			realisedSales: (item.realisedSales ?? 0) === 0 ? null : (item.realisedSales ?? 0),
		}));
	}, [data]);

	// Calculate dynamic interval step based on maximum value in data
	const calculateIntervalStep = (data: TChartDataItemList[]) => {
		if (!data || data.length === 0) return 10;

		const maxValue = Math.max(...data.map((item) => Math.max(item.fairMarketValue ?? 0, item.realisedSales ?? 0)));

		if (maxValue <= 0) return 10;

		// Calculate appropriate step based on max value
		const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
		const normalizedMax = maxValue / magnitude;

		let step;
		if (normalizedMax <= 2) {
			step = magnitude * 0.2;
		} else if (normalizedMax <= 5) {
			step = magnitude * 0.5;
		} else {
			step = magnitude;
		}

		// Ensure we have a reasonable number of intervals (5-10)
		while (maxValue / step > 10) {
			step *= 2;
		}
		while (maxValue / step < 5) {
			step /= 2;
		}

		return Math.max(step, 1);
	};

	const intervalStep = useMemo(() => calculateIntervalStep(sanitizedData), [sanitizedData]);

	const options = useMemo<AgChartOptions>(
		() => ({
			data: sanitizedData,
			series: [
				{
					type: 'line',
					xKey: 'month',
					yKey: 'fairMarketValue',
					yName: monthlyChart.expressions?.fairMarketValue,
					strokeWidth: 3,
					marker: {
						enabled: false,
					},
					stroke: '#00355f',
					interpolation: { type: 'linear' },
				},
				{
					type: 'scatter',
					xKey: 'month',
					yKey: 'realisedSales',
					yName: monthlyChart.expressions?.realisedSales,
					fill: '#F0D035',
					stroke: '#F0D035',
				},
			],
			axes: [
				{
					type: 'category',
					position: 'bottom',
					label: {
						rotation: -90,
					},
					title: {
						text: 'Month',
					},
				},
				{
					type: 'number',
					position: 'left',
					title: {
						text: '$ Mn',
					},
					nice: true,
					min: 0,
					interval: { step: intervalStep },
				},
			],
			padding: { bottom: 10, left: 10, right: 10 },
		}),
		[sanitizedData, monthlyChart.expressions, intervalStep],
	);

	return (
		<Card className="h-full shadow-none border border-msi-darkgrey-color-300 rounded">
			<CardBody className="p-0">
				<ExpandableContainer panelOffsets={panelOffset} title="Monthly Historical Value">
					<ChartContainer data={options.data} filename="monthly_historical_value" buttonPosition="top-right">
						<AgCharts options={options} className="w-full h-full" />
					</ChartContainer>
				</ExpandableContainer>
			</CardBody>
		</Card>
	);
};

export default MonthlyChart;
